import { useMemo } from "react";
import { Button } from "@/core/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/core/components/ui/select";
import { Stack } from "@/core/components/ui/stack";
import {
  TimeRangePicker,
  type TimeRangePreset,
} from "@/core/components/ui/time-range-picker";
import type { ApDayRange } from "@/accounts-payable/query";
import type { ApDayField, ApDocumentType } from "@/accounts-payable/types";
import { apToday } from "@/accounts-payable/utils";
import { dayjs } from "@/core/utils/date";
import { useApBusinessTimeZone } from "./use-ap-business-time-zone";

const DAY = "YYYY-MM-DD";

// The picker works in local-midnight Dates; a day string maps to and from one
// without passing through any time zone.
const toDate = (day: string) => dayjs(day, DAY).toDate();
const toDay = (date: Date) => dayjs(date).format(DAY);

const presetsFor = (today: string): TimeRangePreset[] => {
  const day = (offset: number) =>
    toDate(dayjs(today, DAY).subtract(offset, "day").format(DAY));
  const preset = (label: string, from: Date, to: Date): TimeRangePreset => ({
    label,
    getValue: () => ({ from, to, label }),
  });
  return [
    preset("Today", day(0), day(0)),
    preset("Yesterday", day(1), day(1)),
    preset("Last 7 days", day(6), day(0)),
    preset("Last 30 days", day(29), day(0)),
  ];
};

/** Which date a document list is filtered on. */
export type DayField = ApDayField;

const FIELD_LABEL: Record<DayField, string> = {
  added: "Added",
  reviewed: "Reviewed",
};

/**
 * Filters a document list by the day it was added, or the day it became
 * reviewed, in the business's time zone, so "Today" means the business's
 * today wherever the viewer sits. "Reviewed" + today is the daily batch of
 * invoices ready to bill.
 */
export const DayFilter = ({
  kind,
  field,
  onFieldChange,
  value,
  onChange,
}: {
  kind: ApDocumentType;
  field: DayField;
  onFieldChange: (field: DayField) => void;
  value: ApDayRange | null;
  onChange: (range: ApDayRange | null) => void;
}) => {
  const today = apToday(useApBusinessTimeZone());
  const isToday = value?.from === today && value?.to === today;

  // Both are effect dependencies inside the picker, which re-seeds its
  // in-progress selection whenever they change. Rebuilt on every render, the
  // list's 60s poll would wipe a range someone is halfway through picking.
  const presets = useMemo(() => presetsFor(today), [today]);
  const from = value?.from;
  const to = value?.to;
  const pickerValue = useMemo(
    () =>
      from && to ? { from: toDate(from), to: toDate(to), label: "" } : null,
    [from, to],
  );

  return (
    // The picker's trigger shows only the range ("Oct 6"); the group gives it
    // and the Today toggle their accessible name.
    <Stack
      direction="row"
      gap="xs"
      align="center"
      role="group"
      aria-label={`${FIELD_LABEL[field]} date`}
    >
      <Select
        value={field}
        onValueChange={(next) => onFieldChange(next as DayField)}
      >
        <SelectTrigger aria-label="Filter by date" className="w-36">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {(Object.keys(FIELD_LABEL) as DayField[]).map((option) => (
            <SelectItem key={option} value={option}>
              {FIELD_LABEL[option]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <TimeRangePicker
        placeholder="Any day"
        presets={presets}
        value={pickerValue}
        onChange={(range) =>
          onChange(
            range ? { from: toDay(range.from), to: toDay(range.to) } : null,
          )
        }
      />
      <Button
        size="sm"
        variant={isToday ? "default" : "tertiary"}
        aria-pressed={isToday}
        onClick={() => onChange(isToday ? null : { from: today, to: today })}
      >
        {/* Worded as what you get, not just "Today": next to "Added Sep 1"
            a bare "Today" read as the selected value rather than a shortcut. */}
        Today’s {kind}s
      </Button>
    </Stack>
  );
};
